/* EarthBased scroll-performance patch - 23 Aug 2026 (v1)

   WHAT THIS FIXES
   Two functions in assets/theme.js re-do expensive setup work on EVERY
   scroll event. On a mid-range Android phone this degrades to unusable
   within seconds of scrolling a collection page.

   1. scrollFunction()  (theme.js ~line 3650)
        function scrollFunction(doc = document) {
          doc.addEventListener("scroll", function () {
            scrollFunction();            <-- registers ANOTHER listener
          });
          let mybutton = doc.getElementById("btn-back-to-top");
          mybutton.addEventListener("click", btyScrollToTop);   <-- no null check
      It is called from the page's own scroll handler, so each scroll event
      adds one more document scroll listener AND one more click listener.
      Listener count grows without bound while the shopper scrolls.
      The missing null check also throws on every scroll event when the
      back-to-top button is not rendered.

   2. btyAnimationImageLoad()  (theme.js ~line 3672)
      Called from the same scroll handler. Each call re-queries every
      .lazy-image and constructs a BRAND NEW IntersectionObserver, observing
      them all again. Old observers are never disconnected, so hundreds pile
      up watching the same nodes. IntersectionObserver exists precisely so
      that no scroll listener is needed at all.

   WHY A PATCH FILE INSTEAD OF EDITING theme.js
   theme.js is 97 KB and the Shopify Admin API only writes whole files, so
   editing it here would mean re-transmitting the entire file and risking a
   transcription error in the theme's core script. Both functions are plain
   globals on `window` (theme.js is not wrapped in an IIFE), so replacing
   them from outside is exact and reversible - delete this file and the
   original behaviour returns.

   LOAD ORDER MATTERS - DO NOT CHANGE IT
   This file must be `defer` and must appear AFTER the theme.js tag.
   `defer` scripts run in document order, so theme.js parses first (which
   creates the two globals via function-declaration hoisting), then this
   file replaces them, then DOMContentLoaded fires and theme.js's own
   handler calls the REPLACED versions. Making this script async, or moving
   it above theme.js in snippets/rapid-search-settings.liquid, silently
   restores both bugs.

   BEHAVIOUR IS INTENTIONALLY IDENTICAL
   The back-to-top button still appears past 20px of scroll and still jumps
   to the top on click. Lazy images still swap in data-src / data-srcset and
   still gain the .lazyloaded class. Only the setup is done once instead of
   once per scroll event. */
(function () {
	'use strict';

	/* ---------------------------------------------------------------
	   1. Back-to-top button
	   --------------------------------------------------------------- */
	var btnBound = false;
	var btnFrame = 0;

	function paintBackToTop() {
		btnFrame = 0;
		var btn = document.getElementById('btn-back-to-top');
		if (!btn) return;
		var y = window.pageYOffset ||
			document.documentElement.scrollTop ||
			document.body.scrollTop || 0;
		btn.style.display = y > 20 ? 'flex' : 'none';
	}

	/* Coalesce to one paint per animation frame. Without this the style
	   write happens on every scroll event, which on a 120Hz phone is far
	   more often than the screen can show. */
	function queueBackToTop() {
		if (btnFrame) return;
		btnFrame = window.requestAnimationFrame
			? window.requestAnimationFrame(paintBackToTop)
			: window.setTimeout(paintBackToTop, 16);
	}

	window.scrollFunction = function () {
		if (btnBound) { paintBackToTop(); return; }
		btnBound = true;

		var btn = document.getElementById('btn-back-to-top');
		if (btn) {
			btn.addEventListener('click', function () {
				window.scrollTo(0, 0);
			});
		}

		/* passive:true tells the browser this listener will never call
		   preventDefault(), so it can keep scrolling on the compositor
		   instead of waiting for JS. Supported everywhere at our baseline;
		   the try/catch covers browsers that reject the options object. */
		try {
			window.addEventListener('scroll', queueBackToTop, { passive: true });
		} catch (e) {
			window.addEventListener('scroll', queueBackToTop);
		}

		paintBackToTop();
	};

	/* ---------------------------------------------------------------
	   2. Lazy-image observer
	   --------------------------------------------------------------- */
	var imgObserver = null;

	function reveal(entry, observer) {
		var el = entry.target;
		if (el.dataset.src) el.src = el.dataset.src;
		if (el.dataset.srcSet) el.srcSet = el.dataset.srcSet;
		observer.unobserve(el);
		el.classList.add('lazyloaded');
		el.removeAttribute('data-src');
		el.removeAttribute('data-srcset');
	}

	window.btyAnimationImageLoad = function (doc) {
		doc = doc || document;

		if (typeof window.IntersectionObserver !== 'function') return;

		/* ONE observer for the life of the page, reused across every call.
		   theme.js legitimately re-calls this on shopify:section:load, so
		   new images must still get picked up - hence the per-node flag
		   rather than a run-once guard on the whole function. */
		if (!imgObserver) {
			imgObserver = new IntersectionObserver(function (entries, observer) {
				for (var i = 0; i < entries.length; i++) {
					if (entries[i].isIntersecting) reveal(entries[i], observer);
				}
			}, { threshold: 0 });
		}

		var images = doc.querySelectorAll('.lazy-image');
		for (var i = 0; i < images.length; i++) {
			if (images[i].getAttribute('data-eb-observed') === '1') continue;
			images[i].setAttribute('data-eb-observed', '1');
			imgObserver.observe(images[i]);
		}
	};

	window.EB_PERF_PATCH = true;
})();